import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { authLogin, authLogout, authMe, authRegister, authExchangeGoogleSession, mergeMyRidden } from "./api";

/**
 * Three states:
 *   null  = still checking session
 *   false = not authenticated
 *   {...} = signed-in user
 */
const AuthContext = createContext(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const googleSignInHref = (returnPath = "/passport") =>
    `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + returnPath)}`;

const readGuestRidden = () => {
    const out = {};
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key || !key.startsWith("mri:ridden:")) continue;
            const slug = key.slice("mri:ridden:".length);
            const raw = localStorage.getItem(key);
            if (raw) {
                try {
                    const arr = JSON.parse(raw);
                    if (Array.isArray(arr) && arr.length) out[slug] = arr;
                } catch {}
            }
        }
    } catch {}
    return out;
};

const clearGuestRidden = () => {
    try {
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k && k.startsWith("mri:ridden:")) keys.push(k);
        }
        keys.forEach((k) => localStorage.removeItem(k));
    } catch {}
};

const migrateGuestProgress = async () => {
    const ridden = readGuestRidden();
    if (Object.keys(ridden).length === 0) return { added: 0 };
    try {
        const res = await mergeMyRidden(ridden);
        clearGuestRidden();
        return res;
    } catch (e) {
        return { added: 0, error: e };
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // null = checking

    const checkAuth = useCallback(async () => {
        // If we're returning from Google OAuth, defer to AuthCallback which will handle the session exchange.
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setUser(false);
            return;
        }
        try {
            const { user: me } = await authMe();
            setUser(me);
        } catch {
            setUser(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = useCallback(async (email, password) => {
        const { user: me } = await authLogin({ email, password });
        setUser(me);
        await migrateGuestProgress();
        return me;
    }, []);

    const register = useCallback(async (email, password, name) => {
        const { user: me } = await authRegister({ email, password, name });
        setUser(me);
        await migrateGuestProgress();
        return me;
    }, []);

    const exchangeGoogleSession = useCallback(async (session_id) => {
        const { user: me } = await authExchangeGoogleSession(session_id);
        setUser(me);
        await migrateGuestProgress();
        return me;
    }, []);

    const logout = useCallback(async () => {
        try {
            await authLogout();
        } finally {
            setUser(false);
        }
    }, []);

    return (
        <AuthContext.Provider value={{ user, isAuthed: !!user, login, register, exchangeGoogleSession, logout, refresh: checkAuth }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
};
